import { addDaysYmd } from "./hrUi";

// Pure helpers for the roster and attendance screens.

/* ------------------------------ roster board ------------------------------ */

export const WEEKDAYS = [
  [1, "Mon"],
  [2, "Tue"],
  [3, "Wed"],
  [4, "Thu"],
  [5, "Fri"],
  [6, "Sat"],
  [7, "Sun"],
];

// n consecutive dates from `from`.
export const daysFrom = (from, n) => Array.from({ length: n }, (_, i) => addDaysYmd(from, i));

// ISO weekday number (1 = Monday) of a YYYY-MM-DD date.
export function isoWeekday(ymd) {
  const d = new Date(`${ymd}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

// The Monday on or before a date.
export const mondayOf = (ymd) => addDaysYmd(ymd, -(isoWeekday(ymd) - 1));

// What a board cell shows. cell: { shift_id, status, source, leave:{fraction,status}, holiday:{name,type} } | undefined.
// Precedence: leave, then the roster entry (a shift or a day off), then a holiday, then nothing.
export function cellInfo(cell, shiftsById) {
  const c = cell || {};
  const draft = c.status === "DRAFT";
  if (c.leave) return { kind: "LEAVE", label: c.leave.fraction < 1 ? "½ LV" : "LV", title: `On leave${c.leave.status === "PENDING" ? " (waiting for approval)" : ""}`, color: "#DBEAFE", text: "#1E40AF", draft, pending: c.leave.status === "PENDING" };
  if (c.shift_id) {
    const s = shiftsById.get(c.shift_id);
    return { kind: "SHIFT", label: s ? s.code : "?", title: s ? `${s.name} ${String(s.start_time).slice(0, 5)}-${String(s.end_time).slice(0, 5)}` : "Shift", color: (s && s.color) || "#3b82f6", text: "#FFFFFF", draft, holiday: !!c.holiday };
  }
  if ("shift_id" in c && c.status) return { kind: "OFF", label: "OFF", title: "Day off", color: "#F3F4F6", text: "#6B7280", draft };
  if (c.holiday) return { kind: "HOLIDAY", label: "HOL", title: c.holiday.name, color: "#EDE9FE", text: "#5B21B6", draft: false };
  return { kind: "EMPTY", label: "", title: "Nothing planned", color: "transparent", text: "#9CA3AF", draft: false };
}

// The request body for saving a person's shift over a date range: one entry per day. shiftId null = a day off.
export function buildEntriesPayload({ employeeId, from, to, shiftId, overrideReason }) {
  if (!employeeId || !from || !to || to < from) return null;
  const entries = [];
  for (let d = from; d <= to; d = addDaysYmd(d, 1)) entries.push({ employee_id: employeeId, date: d, shift_id: shiftId || null });
  return { entries, ...(overrideReason ? { override_reason: overrideReason.trim() } : {}) };
}

// How many of each shift (and days off) a board shows for a day: the coverage row under the grid.
export function coverageByDay(people, days) {
  const out = {};
  for (const d of days) out[d] = { working: 0, off: 0, leave: 0 };
  for (const p of people) {
    for (const d of days) {
      const c = p.cells[d];
      if (!c) continue;
      if (c.leave) out[d].leave += 1;
      else if (c.shift_id) out[d].working += 1;
      else if ("shift_id" in c && c.status) out[d].off += 1;
    }
  }
  return out;
}

/* ------------------------------ patterns ------------------------------ */

const cell = (id) => ({ shift_id: id || null });

// The form the pattern drawer edits -> what the API takes.
// form: { name, pattern_type, weeks:[ {1..7: shiftId|null} ], blocks:[{shift_id, days}], on_days, off_days, shift_id }
export function buildPatternPayload(form) {
  const base = { name: (form.name || "").trim(), pattern_type: form.pattern_type };
  if (form.pattern_type === "FIXED_WEEKLY") {
    return { ...base, weeks: (form.weeks || []).map((w) => WEEKDAYS.map(([d]) => cell(w[d]))) };
  }
  if (form.pattern_type === "ROTATING") {
    return { ...base, blocks: (form.blocks || []).map((b) => ({ shift_id: b.shift_id || null, days: Number(b.days) })) };
  }
  return { ...base, on_days: Number(form.on_days), off_days: Number(form.off_days), shift_id: form.shift_id || null };
}

// The API's pattern (source) -> the form. Weeks come back as arrays of { shift_id }.
export function patternToForm(p) {
  const src = p.source || {};
  if (p.pattern_type === "FIXED_WEEKLY") {
    const weeks = (src.weeks || []).map((w) => Object.fromEntries(WEEKDAYS.map(([d], i) => [d, (w[i] && w[i].shift_id) || null])));
    return { name: p.name, pattern_type: p.pattern_type, weeks: weeks.length ? weeks : [emptyWeek()] };
  }
  if (p.pattern_type === "ROTATING") return { name: p.name, pattern_type: p.pattern_type, blocks: (src.blocks || []).map((b) => ({ shift_id: b.shift_id || null, days: b.days })), weeks: [emptyWeek()] };
  return { name: p.name, pattern_type: p.pattern_type, on_days: src.on_days, off_days: src.off_days, shift_id: src.shift_id || null, weeks: [emptyWeek()] };
}

export const emptyWeek = () => Object.fromEntries(WEEKDAYS.map(([d]) => [d, null]));

// A one-line description of a saved pattern for the list.
export function describePattern(p, shiftsById) {
  const code = (id) => (id && shiftsById.get(id) ? shiftsById.get(id).code : "?");
  const src = p.source || {};
  if (p.pattern_type === "ON_OFF") return `${src.on_days} on / ${src.off_days} off, ${code(src.shift_id)}`;
  if (p.pattern_type === "ROTATING") return (src.blocks || []).map((b) => `${b.days} days ${b.shift_id ? code(b.shift_id) : "off"}`).join(", then ");
  return (src.weeks || [])
    .map((w) => {
      const parts = [];
      let i = 0;
      while (i < 7) {
        const id = w[i] && w[i].shift_id;
        if (!id) {
          i += 1;
          continue;
        }
        let j = i;
        while (j + 1 < 7 && w[j + 1] && w[j + 1].shift_id === id) j += 1;
        parts.push(`${i === j ? WEEKDAYS[i][1] : `${WEEKDAYS[i][1]}-${WEEKDAYS[j][1]}`} ${code(id)}`);
        i = j + 1;
      }
      return parts.join(", ") || "No working days";
    })
    .join(" / ");
}

/* ------------------------------ muster roll ------------------------------ */

// The muster as rows for a spreadsheet: a header, then one row per person with each day's status code and the totals.
export function musterSheet(muster, statusCode) {
  const days = [];
  for (let d = muster.from; d <= muster.to; d = addDaysYmd(d, 1)) days.push(d);
  const header = ["Code", "Name", "Department", ...days.map((d) => d.slice(8)), "Present", "Half days", "Absent", "Leave", "Late marks", "OT (min)"];
  const rows = muster.people.map((p) => [
    p.employee_code,
    p.full_name,
    p.department_name || "",
    ...days.map((d) => (p.days[d] ? statusCode(p.days[d].status) : "")),
    p.summary.present,
    p.summary.half_days,
    p.summary.absent,
    p.summary.on_leave,
    p.summary.late_marks,
    p.summary.ot_minutes,
  ]);
  return [header, ...rows];
}
